import type { AgentToolUseBlock, TaskDesignFreedom } from "@calcplan/ai-capabilities";

export type TaskExecutionProfile = "direct" | "guided" | "open";

export interface TurnLimits {
  softModelCalls: number;
  softInputTokens: number;
  emergencyModelCalls: number;
  emergencyToolCalls: number;
  emergencyInputTokens: number;
  emergencyActiveMs: number;
  maxCapturesPerBlock: number;
  maxVisualRoundsWithoutMutation: number;
  maxCorrectionCyclesPerIssue: number;
  maxNoProgressRounds: number;
}

export interface TurnBudgetSnapshot {
  phase: "direct" | "discovery" | "planning" | "execution" | "verification";
  hasPendingStep: boolean;
  pendingStepMutationAttempts: number;
  recoveryAttempts: number;
  modelCalls: number;
  toolCalls: number;
  inputTokens: number;
  activeMs: number;
  captureCalls: number;
  visualRoundsWithoutMutation: number;
  correctionCycles: number;
  noProgressRounds: number;
}

export interface TurnBudgetDecision {
  softLimitReached: boolean;
  hardLimitReason: string | null;
}

const LIMITS: Record<TaskExecutionProfile, TurnLimits> = {
  direct: {
    softModelCalls: 6,
    softInputTokens: 140_000,
    emergencyModelCalls: 80,
    emergencyToolCalls: 240,
    emergencyInputTokens: 4_000_000,
    emergencyActiveMs: 2 * 60 * 60_000,
    maxCapturesPerBlock: 8,
    maxVisualRoundsWithoutMutation: 4,
    maxCorrectionCyclesPerIssue: 2,
    maxNoProgressRounds: 4,
  },
  guided: {
    softModelCalls: 12,
    softInputTokens: 350_000,
    emergencyModelCalls: 240,
    emergencyToolCalls: 800,
    emergencyInputTokens: 12_000_000,
    emergencyActiveMs: 6 * 60 * 60_000,
    maxCapturesPerBlock: 16,
    maxVisualRoundsWithoutMutation: 6,
    maxCorrectionCyclesPerIssue: 3,
    maxNoProgressRounds: 7,
  },
  open: {
    softModelCalls: 18,
    softInputTokens: 600_000,
    emergencyModelCalls: 360,
    emergencyToolCalls: 1_200,
    emergencyInputTokens: 18_000_000,
    emergencyActiveMs: 8 * 60 * 60_000,
    maxCapturesPerBlock: 24,
    maxVisualRoundsWithoutMutation: 7,
    maxCorrectionCyclesPerIssue: 4,
    maxNoProgressRounds: 9,
  },
};

export function taskExecutionProfile(needsPlanning: boolean, designFreedom: TaskDesignFreedom): TaskExecutionProfile {
  if (!needsPlanning) return "direct";
  return designFreedom === "open" ? "open" : "guided";
}

export function turnLimits(profile: TaskExecutionProfile): TurnLimits {
  return LIMITS[profile];
}

export function evaluateTurnBudget(profile: TaskExecutionProfile, snapshot: TurnBudgetSnapshot): TurnBudgetDecision {
  const limits = turnLimits(profile);
  // Totais acumulados não medem progresso e, portanto, não devem encerrar uma
  // tarefa normal. Estes tetos são apenas disjuntores contra execuções
  // catastróficas; o controle cotidiano é feito pelos sinais de estagnação.
  const pendingExecutionCanRecover = snapshot.phase === "execution" && snapshot.hasPendingStep;
  const exhaustedPendingRecovery = pendingExecutionCanRecover
    && snapshot.pendingStepMutationAttempts >= 2
    && snapshot.recoveryAttempts >= 4;
  const hardLimitReason = snapshot.noProgressRounds >= limits.maxNoProgressRounds
    && (!pendingExecutionCanRecover || exhaustedPendingRecovery)
    ? "A tarefa deixou de produzir progresso verificável depois de estratégias concretas de execução."
    : snapshot.phase === "verification"
      && snapshot.visualRoundsWithoutMutation >= limits.maxVisualRoundsWithoutMutation
      ? "A inspeção visual deixou de produzir evidência nova."
      // A correção acontece na fase de execução (o laudo devolve o turno para
      // lá), então este disjuntor não pode depender da fase: sem isso, um ciclo
      // reprova → corrige → reprova só terminaria nos disjuntores de emergência.
      : snapshot.correctionCycles > limits.maxCorrectionCyclesPerIssue
        ? "A mesma divergência continuou depois de diferentes estratégias de correção."
        : snapshot.modelCalls >= limits.emergencyModelCalls
          ? "A tarefa atingiu o disjuntor de emergência de decisões do modelo."
          : snapshot.toolCalls >= limits.emergencyToolCalls
            ? "A tarefa atingiu o disjuntor de emergência de operações."
            : snapshot.inputTokens >= limits.emergencyInputTokens
              ? "A tarefa atingiu o disjuntor de emergência de contexto acumulado."
              : snapshot.activeMs >= limits.emergencyActiveMs
                ? "A tarefa atingiu o disjuntor de emergência de tempo efetivo de processamento."
                : null;
  return {
    hardLimitReason,
    softLimitReached: !hardLimitReason && (
      snapshot.modelCalls >= limits.softModelCalls
      || snapshot.inputTokens >= limits.softInputTokens
      || snapshot.captureCalls >= Math.max(1, limits.maxCapturesPerBlock - 2)
    ),
  };
}

export function activeTurnMs(input: { absoluteStartedAt: number; waitingMs: number; waitingSince: number | null }, now = Date.now()): number {
  const currentWait = input.waitingSince === null ? 0 : Math.max(0, now - input.waitingSince);
  return Math.max(0, now - input.absoluteStartedAt - input.waitingMs - currentWait);
}

export function turnCanCloseCompleted(input: {
  phase: "direct" | "discovery" | "planning" | "execution" | "verification";
  pendingCount: number;
  sceneMutated: boolean;
  visualReviewDone: boolean;
  hasCompletionEvidence: boolean;
}): boolean {
  return input.phase !== "discovery" && input.phase !== "planning"
    && input.hasCompletionEvidence
    && input.pendingCount === 0
    && (!input.sceneMutated || input.visualReviewDone);
}

export function callBatchSignature(calls: AgentToolUseBlock[]): string {
  return calls.map((call) => `${call.name}:${stableValue(semanticCallInput(call.name, call.input))}`).join("|");
}

export function advanceBatchRecurrence(
  state: { lastSignature: string | null; recentSignatures: string[]; repeatedRounds: number },
  calls: AgentToolUseBlock[],
): { lastSignature: string | null; recentSignatures: string[]; repeatedRounds: number } {
  const signature = callBatchSignature(calls);
  if (!signature) return state;
  const recentOccurrences = state.recentSignatures.filter((item) => item === signature).length;
  return {
    lastSignature: signature,
    recentSignatures: [...state.recentSignatures, signature].slice(-6),
    repeatedRounds: signature === state.lastSignature || recentOccurrences >= 2
      ? state.repeatedRounds + 1
      : 0,
  };
}

/**
 * Assinatura estável de uma reprovação: os alvos e os aspectos que falharam,
 * não a prosa do laudo. O crítico reescreve a mesma objeção a cada rodada; sem
 * isto, "a mesma divergência" nunca era reconhecida como repetida e o disjuntor
 * de ciclos de correção jamais disparava.
 */
export function verificationIssueSignature(issues: ReadonlyArray<string>): string {
  const tokens = new Set<string>();
  for (const issue of issues) {
    const normalized = issue
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLocaleLowerCase("pt-BR");
    for (const match of normalized.matchAll(/element-[a-z0-9-]{6,}/g)) tokens.add(match[0]);
    for (const match of normalized.matchAll(/falha (?:em|de) ([a-z_]+)/g)) tokens.add(`aspect:${match[1]}`);
    for (const marker of ["emenda-de-plano-necessaria", "sobreposicao", "apoio", "colisao", "cobertura"]) {
      if (normalized.includes(marker)) tokens.add(`flag:${marker}`);
    }
  }
  if (!tokens.size) {
    for (const issue of issues) tokens.add(issue.slice(0, 60));
  }
  return [...tokens].sort().join("|");
}

export function isVisualOnlyBatch(calls: AgentToolUseBlock[]): boolean {
  return calls.length > 0 && calls.every((call) => [
    "scene.viewport.capture",
    "scene.viewport.review",
    "scene.check.clearance",
    "scene.check.grounded",
    "scene.check.installation",
    "scene.element.read",
  ].includes(call.name));
}

export function visualEvidenceSignature(capabilityName: string, value: unknown): string | null {
  const result = asRecord(value);
  if (!result) return null;
  if (capabilityName === "scene.viewport.review") {
    return stableValue({
      kind: "review",
      acceptedIds: sortedStrings(result.acceptedIds),
      rejectedIds: sortedStrings(result.rejectedIds),
      unverifiableIds: sortedStrings(result.unverifiableIds),
      remainingIds: sortedStrings(result.remainingIds),
      coverageComplete: result.coverageComplete,
      issues: sortedStrings(result.issues),
      environmentStatus: result.environmentStatus,
      protectedObservations: sortedStrings(result.protectedObservations),
    });
  }
  if (capabilityName !== "scene.viewport.capture") return null;
  const visualReview = asRecord(result.visualReview);
  const targets = Array.isArray(visualReview?.targets)
    ? visualReview.targets.map((target) => {
        const record = asRecord(target) ?? {};
        return {
          elementId: record.elementId,
          status: record.status,
          qualifiedViewCount: record.qualifiedViewCount,
          bestVisibilityRatio: rounded(record.bestVisibilityRatio),
          bestFrameCoverageRatio: rounded(record.bestFrameCoverageRatio),
        };
      }).sort((left, right) => String(left.elementId).localeCompare(String(right.elementId)))
    : [];
  const inspection = Array.isArray(result.inspection)
    ? result.inspection.map((item) => {
        const record = asRecord(item) ?? {};
        const support = asRecord(record.support);
        const contacts = Array.isArray(record.contacts)
          ? record.contacts.map((contact) => {
              const candidate = asRecord(contact) ?? {};
              return {
                kind: candidate.kind,
                targetId: candidate.targetId,
                status: candidate.status,
                renderedGapMm: rounded(candidate.renderedGapMm),
                logicalGapMm: rounded(candidate.logicalGapMm),
              };
            }).sort((left, right) => `${left.kind}:${left.targetId}`.localeCompare(`${right.kind}:${right.targetId}`))
          : [];
        return {
          elementId: record.elementId,
          support: support ? {
            kind: support.kind,
            surfaceId: support.surfaceId,
            status: support.status,
            renderedGapMm: rounded(support.renderedGapMm),
            logicalGapMm: rounded(support.logicalGapMm),
          } : null,
          contacts,
        };
      }).sort((left, right) => String(left.elementId).localeCompare(String(right.elementId)))
    : [];
  return stableValue({
    kind: "capture",
    context: result.context,
    fittedIds: sortedStrings(result.fittedIds),
    coverageComplete: result.coverageComplete,
    status: visualReview?.status,
    coveredIds: sortedStrings(visualReview?.coveredIds),
    issues: sortedStrings(visualReview?.issues),
    targets,
    inspection,
  });
}

export function mutationOutcomeSignature(capabilityName: string, value: unknown): string | null {
  const result = asRecord(value);
  if (!result) return null;
  if (capabilityName === "scene.module.add") {
    const element = asRecord(result.element);
    const catalogRef = asRecord(element?.catalogRef);
    return stableValue({
      kind: "module-intent",
      stepId: result.stepId,
      instanceKey: result.instanceKey,
      catalogItemId: catalogRef?.itemId,
    });
  }
  if (capabilityName === "scene.element.delete" && Array.isArray(result.deletedCreationIntents)) {
    return stableValue({
      kind: "creation-intent-deletion",
      intents: result.deletedCreationIntents,
    });
  }
  const element = compactMutationElement(result.element);
  const elements = Array.isArray(result.elements) ? result.elements.map(compactMutationElement).filter(Boolean) : [];
  const signature = {
    kind: capabilityName === "scene.part.interact" ? "interaction" : capabilityName === "scene.element.delete" ? "deletion" : "element-state",
    element,
    elements,
    deletedIds: sortedStrings(result.deletedIds),
    materialName: result.materialName,
    interaction: result.interaction,
    allInteractionsClosed: result.allInteractionsClosed,
    wallId: result.wallId,
    face: result.face,
  };
  return element || elements.length || (signature.deletedIds as string[]).length || result.interaction
    ? stableValue(signature)
    : null;
}

export function advanceSpatialMutationHistory(
  previous: string[],
  capabilityName: string,
  value: unknown,
): { history: string[]; revisited: boolean } {
  if (!["scene.element.move", "scene.element.nudge", "scene.element.mount", "scene.element.rotate", "scene.element.place", "scene.module.install"].includes(capabilityName)) {
    return { history: previous, revisited: false };
  }
  const result = asRecord(value);
  const element = asRecord(result?.element);
  if (!element || typeof element.id !== "string") return { history: previous, revisited: false };
  const history = [...previous];
  const push = (translation: unknown, rotation: unknown) => {
    if (!Array.isArray(translation)) return;
    const signature = stableValue({ id: element.id, translation, rotation: Array.isArray(rotation) ? rotation : element.rotationRad });
    if (!history.includes(signature)) history.push(signature);
  };
  const previousTransform = asRecord(result?.previousTransform);
  push(result?.previousTranslationMm ?? previousTransform?.translationMm, result?.previousRotationRad ?? previousTransform?.rotationRad);
  const current = stableValue({ id: element.id, translation: element.translationMm, rotation: element.rotationRad });
  const revisited = history.includes(current);
  if (!revisited) history.push(current);
  return { history: history.slice(-32), revisited };
}

function semanticCallInput(name: string, value: unknown): unknown {
  const input = asRecord(value);
  if (!input) return value;
  if (name === "scene.viewport.capture") {
    return { fit: sortedStrings(input.fit), context: input.context, view: input.view, occlusionMode: input.occlusionMode };
  }
  if (name === "task.plan.progress") return { stepId: input.stepId, status: input.status };
  if (name === "catalog.item.search") return { query: input.query, type: input.type };
  if (name === "skill.load") return { slug: input.slug };
  if (name === "scene.module.add") {
    return {
      catalogItemId: input.catalogItemId,
      instanceKey: input.instanceKey ?? "primary",
    };
  }
  if (name.startsWith("scene.element.") || name.startsWith("scene.check.")) {
    return {
      elementId: input.elementId,
      elementIds: sortedStrings(input.elementIds),
      catalogItemId: input.catalogItemId,
      wallId: input.wallId,
      path: input.path,
      desiredState: input.desiredState,
    };
  }
  return input;
}

function compactMutationElement(value: unknown): unknown {
  const element = asRecord(value);
  if (!element) return null;
  return {
    id: element.id,
    translationMm: element.translationMm,
    rotationRad: element.rotationRad,
    dimensionsMm: element.dimensionsMm,
    renderedDimensionsMm: element.renderedDimensionsMm,
    materialId: element.materialId,
    visible: element.visible,
  };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function sortedStrings(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string").sort() : [];
}

function rounded(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? Math.round(value * 10) / 10 : null;
}

function stableValue(value: unknown): string {
  try {
    return JSON.stringify(sortValue(value));
  } catch {
    return String(value);
  }
}

function sortValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortValue);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(Object.entries(value as Record<string, unknown>)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, item]) => [key, sortValue(item)]));
}
